import { endpointFormatter } from 'utils/endpoint';
import IntervalCatalog from 'utils/enums/interval';

/** ---------- group → dashboard label mapping ---------- */
// Backend deviceGroupName -> Dashboard display bucket
const GROUP_TO_DISPLAY = {
  ACLOAD: 'ATS',
  CDEMAND: 'BDC',
  LOCALIO: 'METERS',
  PDC: 'PDC',
  BATT: 'BATT',
  EV: 'EV',
  FLEET: 'FLEET',
  RENGINE: 'RENGINE',
};

/** ---------- helpers ---------- */

function safeName(x) {
  if (x === undefined || x === null) return 'Unknown';
  return String(x);
}

function sortDonut(a, b) {
  if (b.value !== a.value) return b.value - a.value;
  return safeName(a.name).localeCompare(safeName(b.name));
}

/** ---------- normalizers ---------- */

function normalizeTimeSeries(
  raw,
  {
    seriesKey = 'series',
    pointsKey = 'points',
    dateKey = 'date',
    valueKey = 'value',
    nameKey = 'name',
  } = {},
) {
  const out = [];
  if (!raw) return { chartType: 'lines', data: out };

  // Already flat: [{ date, value, group? }]
  if (
    Array.isArray(raw) &&
    raw.length &&
    raw[0][dateKey] !== undefined &&
    raw[0][valueKey] !== undefined
  ) {
    return {
      chartType: 'lines',
      data: raw.map((d) => ({
        date: d[dateKey],
        value: Number(d[valueKey]),
        group: d.group || d[nameKey] || 'Series',
      })),
    };
  }

  // Shape: { series: [{ name, points: [{ date, value }] }] }
  if (raw[seriesKey] && Array.isArray(raw[seriesKey])) {
    raw[seriesKey].forEach((s) => {
      const groupName = s[nameKey] || 'Series';
      const pts = s[pointsKey] || [];
      pts.forEach((p) => {
        out.push({ date: p[dateKey], value: Number(p[valueKey]), group: groupName });
      });
    });
    return { chartType: 'lines', data: out };
  }

  // Shape: { Solar: [{ date, value }], Grid: [...] }
  if (!Array.isArray(raw) && typeof raw === 'object') {
    Object.keys(raw).forEach((seriesName) => {
      (raw[seriesName] || []).forEach((p) => {
        out.push({ date: p[dateKey], value: Number(p[valueKey]), group: seriesName });
      });
    });
    return { chartType: 'lines', data: out };
  }

  return { chartType: 'lines', data: out };
}

function normalizeStackedBar(raw, { categoryKey = 'category', stacksKey = 'stacks' } = {}) {
  const data = Array.isArray(raw) ? raw : [];
  return {
    chartType: 'stacked-bar',
    data: data.map((row) => ({
      name: row[categoryKey],
      values: (row[stacksKey] || []).map((s) => ({ name: s.name, value: Number(s.value) })),
    })),
  };
}

/**
 * Devices-by-Type donut to match dashboard taxonomy (PDC, BATT, BDC, INV, METERS, ATS, EV, FLEET, RENGINE).
 * Count devices under each backend group (and subgroups) and label via GROUP_TO_DISPLAY.
 */
function normalizeDevicesByType(raw) {
  const totals = new Map();

  const inc = (name, n = 1) => {
    const key = safeName(name);
    const v = Number.isFinite(n) ? n : 0;
    totals.set(key, (totals.get(key) || 0) + v);
  };

  if (Array.isArray(raw)) {
    // Option A: already aggregated: [{ type: "PDC", count: 5 }, ...]
    if (raw.length && raw[0] && 'type' in raw[0] && 'count' in raw[0]) {
      raw.forEach(({ type, count }) => inc(GROUP_TO_DISPLAY[type] || type, Number(count) || 0));
    } else {
      // Option B: group objects with nested subgroups/devices
      raw.forEach((group) => {
        const rawLabel = group?.deviceGroupName || 'Unknown';
        const label = GROUP_TO_DISPLAY[rawLabel] || rawLabel;

        let count = 0;

        if (Array.isArray(group.devices)) {
          count += group.devices.length;
        }

        if (Array.isArray(group.subgroups)) {
          group.subgroups.forEach((sg) => {
            if (Array.isArray(sg.devices)) count += sg.devices.length;
          });
        }

        // If no explicit device arrays, count the group as 1 to preserve parity with legacy charts.
        if (count === 0) count = 1;

        inc(label, count);
      });
    }
  } else if (raw && Array.isArray(raw.groups)) {
    // Alternate shape: { groups: [...] }
    raw.groups.forEach((g) => {
      const rawLabel = g?.deviceGroupName || 'Unknown';
      const label = GROUP_TO_DISPLAY[rawLabel] || rawLabel;

      let count = 0;
      (g.subgroups || []).forEach((sg) => {
        if (Array.isArray(sg.devices)) count += sg.devices.length;
      });
      if (count === 0) count = 1;

      inc(label, count);
    });
  }

  return {
    chartType: 'donut',
    data: Array.from(totals.entries())
      .map(([name, value]) => ({ name: safeName(name), value }))
      .sort(sortDonut),
  };
}

/** ---------- POST body helper for charts-service families ---------- */
const postChartsBody = (sel) => ({
  sites: sel.siteId ? [sel.siteId] : sel.sites || [],
  startDate: sel.dateRange.from,
  endDate: sel.dateRange.to,
  interval: sel.interval || IntervalCatalog.custom,
  aggregation: sel.aggregation || null,
});

/** ---------- metrics registry (default export) ---------- */
const METRICS = {
  // Energy
  solarProduction: {
    family: 'energy',
    endpointKey: 'fetchEnergyCharts',
    method: 'POST',
    buildRequest: (sel) => ({
      url: endpointFormatter('fetchEnergyCharts'),
      method: 'POST',
      body: postChartsBody(sel),
    }),
    normalizer: (raw) =>
      normalizeTimeSeries(raw, {
        seriesKey: 'series',
        pointsKey: 'points',
        dateKey: 'date',
        valueKey: 'value',
        nameKey: 'name',
      }),
  },
  energyPower: {
    family: 'energy',
    endpointKey: 'fetchEnergyCharts',
    method: 'POST',
    buildRequest: (sel) => ({
      url: endpointFormatter('fetchEnergyCharts'),
      method: 'POST',
      body: postChartsBody(sel),
    }),
    normalizer: (raw) => normalizeTimeSeries(raw),
  },

  // ESS
  essStateOfCharge: {
    family: 'ess',
    endpointKey: 'fetchESSCharts',
    method: 'POST',
    buildRequest: (sel) => ({
      url: endpointFormatter('fetchESSCharts'),
      method: 'POST',
      body: postChartsBody(sel),
    }),
    normalizer: (raw) => normalizeTimeSeries(raw),
  },

  // Facility
  facilityUsage: {
    family: 'facility',
    endpointKey: 'fetchFacilityCharts',
    method: 'POST',
    buildRequest: (sel) => ({
      url: endpointFormatter('fetchFacilityCharts'),
      method: 'POST',
      body: postChartsBody(sel),
    }),
    normalizer: (raw) => normalizeTimeSeries(raw),
  },

  // Devices by Type → Donut (dashboard-parity)
  devicesByType: {
    family: 'devices-by-type',
    endpointKey: 'fetchDevicesBySiteId',
    method: 'GET',
    buildRequest: (sel) => ({
      url: endpointFormatter('fetchDevicesBySiteId', { siteId: sel.siteId }),
      method: 'GET',
      params: {},
    }),
    normalizer: (raw) => normalizeDevicesByType(raw),
  },

  // Device Telemetry → Lines
  deviceTelemetry: {
    family: 'device-telemetry',
    endpointKey: 'fetchTelemetryBySiteIdAndType',
    method: 'GET',
    buildRequest: (sel) => ({
      url: endpointFormatter('fetchTelemetryBySiteIdAndType', {
        siteId: sel.siteId,
        deviceType: sel.deviceType,
      }),
      method: 'GET',
      params: {
        from: sel.dateRange.from,
        to: sel.dateRange.to,
        aggregation: sel.aggregation || 'avg',
        interval: sel.interval || IntervalCatalog.custom,
      },
    }),
    normalizer: (raw) =>
      normalizeTimeSeries(raw, {
        seriesKey: 'series',
        pointsKey: 'points',
        dateKey: 'date',
        valueKey: 'value',
        nameKey: 'name',
      }),
  },

  // Example Stacked Bar
  facilityEnergyMix: {
    family: 'facility',
    endpointKey: 'fetchFacilityCharts',
    method: 'POST',
    buildRequest: (sel) => ({
      url: endpointFormatter('fetchFacilityCharts'),
      method: 'POST',
      body: postChartsBody(sel),
    }),
    normalizer: (raw) => normalizeStackedBar(raw),
  },
};

export default METRICS;
